/**
 * Hook para monitoramento da fila PGMQ financeira (webhooks de pagamento)
 * 
 * Usa as RPCs get_financial_queue_stats_pgmq, get_financial_queue_items_pgmq,
 * get_financial_queue_volume_pgmq, get_financial_queue_slowest_pgmq e
 * get_financial_queue_alerts_pgmq
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Interface para estatísticas da fila financeira */
export interface FinancialQueueStats {
  pending: number;
  processing: number;
  completed24h: number;
  failed24h: number;
  dlqCount: number;
  successRate: number;
  avgProcessingTime: number;
  webhooks24h: number;
}

/** Interface para item da fila financeira */
export interface FinancialQueueItem {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: Record<string, unknown>;
  status: 'pending' | 'processing' | 'archived' | 'dlq';
  tenant_id: string | null;
  source: 'queue' | 'archive' | 'dlq';
  // Campos enriquecidos
  tenant_name?: string;
  event_type?: string;
  provider_slug?: string;
  processing_time?: number;
}

/** Interface para volume por hora */
export interface FinancialQueueVolume {
  hour: string;
  completed: number;
  failed: number;
}

/** Interface para alerta de saúde */
export interface FinancialQueueAlert {
  id: string;
  type: 'warning' | 'error';
  message: string;
  count?: number;
}

/** Interface para item lento */
export interface FinancialQueueSlowestItem {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  processing_time_formatted: string;
  status: 'pending' | 'processing' | 'archived';
  tenant_id: string | null;
  tenant_name?: string;
  event_type?: string;
  provider_slug?: string;
  source: 'queue' | 'archive';
  message?: Record<string, unknown>;
}

// ============================================================================
// INTERFACES DE RESPOSTA DAS RPCS
// ============================================================================

interface StatsRpcResponse {
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  dlq_count: number;
  avg_latency_ms: number;
  webhooks_24h: number;
}

interface ItemRpcResponse {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: Record<string, unknown>;
  status: string;
  tenant_id: string | null;
  source: string;
}

interface VolumeRpcResponse {
  hour_label: string;
  completed_count: number;
  failed_count: number;
}

interface AlertRpcResponse {
  alert_id: string;
  alert_type: string;
  alert_message: string;
  alert_count: number;
}

interface SlowestRpcResponse {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  status: string;
  tenant_id: string | null;
  message: Record<string, unknown>;
  source: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Formata tempo de processamento em string legível */
function formatProcessingTimeMs(ms: number): string {
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook para buscar estatísticas da fila financeira
 */
export function useFinancialQueueStats() {
  return useQuery({
    queryKey: ['financial-queue-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_financial_queue_stats_pgmq' as any);
      if (error) throw error;

      const stats = (data as unknown as StatsRpcResponse[])?.[0];
      if (!stats) {
        return {
          pending: 0, processing: 0, completed24h: 0, failed24h: 0,
          dlqCount: 0, successRate: 100, avgProcessingTime: 0, webhooks24h: 0
        } as FinancialQueueStats;
      }

      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 ? (stats.completed_count / total) * 100 : 100;

      return {
        pending: stats.pending_count || 0,
        processing: stats.processing_count || 0,
        completed24h: stats.completed_count || 0,
        failed24h: stats.failed_count || 0,
        dlqCount: stats.dlq_count || 0,
        successRate,
        avgProcessingTime: (stats.avg_latency_ms || 0) / 1000,
        webhooks24h: stats.webhooks_24h || 0,
      } as FinancialQueueStats;
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar itens da fila financeira
 */
export function useFinancialQueueItems(statusFilter: string = 'all', limit: number = 200) {
  return useQuery({
    queryKey: ['financial-queue-items', statusFilter, limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_financial_queue_items_pgmq' as any, {
        p_status: statusFilter,
        p_limit: limit
      });
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as ItemRpcResponse[];

      // Enriquecimento de tenant names
      const tenantIds = new Set<string>();
      rawItems.forEach(item => { if (item.tenant_id) tenantIds.add(item.tenant_id); });

      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants').select('id, name').in('id', Array.from(tenantIds));
        tenantMap = new Map((tenants || []).map((t: { id: string; name: string }) => [t.id, t.name]));
      }

      return rawItems.map(item => {
        let processingTime: number | undefined;
        if (item.source === 'archive' && item.enqueued_at && item.vt) {
          processingTime = (new Date(item.vt).getTime() - new Date(item.enqueued_at).getTime()) / 1000;
        }
        return {
          msg_id: item.msg_id,
          read_ct: item.read_ct,
          enqueued_at: item.enqueued_at,
          vt: item.vt,
          message: item.message,
          status: item.status as FinancialQueueItem['status'],
          tenant_id: item.tenant_id,
          source: item.source as FinancialQueueItem['source'],
          tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
          event_type: (item.message?.event_type as string) || (item.message?.type as string) || 'unknown',
          provider_slug: (item.message?.provider_slug as string) || '',
          processing_time: processingTime,
        };
      }) as FinancialQueueItem[];
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar volume de processamento por hora
 */
export function useFinancialQueueVolume(hours: number = 24) {
  return useQuery({
    queryKey: ['financial-queue-volume', hours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_financial_queue_volume_pgmq' as any, { hours_ago: hours });
      if (error) throw error;
      return ((data || []) as unknown as VolumeRpcResponse[]).map(item => ({
        hour: item.hour_label,
        completed: item.completed_count || 0,
        failed: item.failed_count || 0
      })) as FinancialQueueVolume[];
    },
    refetchInterval: 60000,
  });
}

/**
 * Hook para gerar alertas de saúde da fila financeira
 * Usa a RPC dedicada que retorna alertas do servidor
 */
export function useFinancialQueueAlerts() {
  return useQuery({
    queryKey: ['financial-queue-alerts'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_financial_queue_alerts_pgmq' as any);
      if (error) throw error;

      const rawAlerts = (data || []) as unknown as AlertRpcResponse[];
      return rawAlerts.map(a => ({
        id: a.alert_id,
        type: a.alert_type as 'warning' | 'error',
        message: a.alert_message,
        count: a.alert_count,
      })) as FinancialQueueAlert[];
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar os TOP N itens mais lentos
 */
export function useFinancialQueueSlowestItems(limit: number = 20) {
  return useQuery({
    queryKey: ['financial-queue-slowest', limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_financial_queue_slowest_pgmq' as any, { p_limit: limit });
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as SlowestRpcResponse[];

      // Enriquecimento de tenant names
      const tenantIds = new Set<string>();
      rawItems.forEach(item => { if (item.tenant_id) tenantIds.add(item.tenant_id); });

      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants').select('id, name').in('id', Array.from(tenantIds));
        tenantMap = new Map((tenants || []).map((t: { id: string; name: string }) => [t.id, t.name]));
      }

      return rawItems.map(item => ({
        msg_id: item.msg_id,
        read_ct: item.read_ct || 0,
        enqueued_at: item.enqueued_at,
        completed_at: item.completed_at,
        processing_time_ms: item.processing_time_ms,
        processing_time_formatted: formatProcessingTimeMs(item.processing_time_ms),
        status: item.status as FinancialQueueSlowestItem['status'],
        tenant_id: item.tenant_id,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
        event_type: (item.message?.event_type as string) || (item.message?.type as string) || 'unknown',
        provider_slug: (item.message?.provider_slug as string) || '',
        source: item.source as FinancialQueueSlowestItem['source'],
        message: item.message || {},
      })) as FinancialQueueSlowestItem[];
    },
    refetchInterval: 30000,
  });
}
